/**
 * Sanificazione del testo destinato ai PDF generati con jsPDF / jspdf-autotable.
 *
 * PERCHE' SERVE
 * I font standard usati da jsPDF (Helvetica & co.) vengono dichiarati nel PDF con
 * "/Encoding /WinAnsiEncoding", cioe' una codifica a UN byte per carattere (cp1252).
 * Se una stringa contiene anche UN SOLO carattere fuori da cp1252, jsPDF non fallisce
 * e non lo scarta: emette l'INTERA stringa come UTF-16BE dentro un font che resta
 * dichiarato WinAnsi. Il lettore PDF rilegge quei byte uno a uno, quindi:
 *
 *   "Ideal Standard → Tesi Sospeso"
 *      -> byte: 00 49 00 64 00 65 ... 21 92 ...
 *      -> reso: "<NUL>I<NUL>d<NUL>e<NUL>a<NUL>l ... !' <NUL>T<NUL>e<NUL>s<NUL>i ..."
 *
 * Da qui i sintomi osservati sui preventivi: lettere distanziate, simboli anomali
 * ("!'", "thorn-y", "O=UI"), larghezza del testo raddoppiata, glifi mancanti.
 * Il danno non e' limitato al carattere incriminato: colpisce tutta la cella.
 *
 * COSA FA
 * Riporta il testo dentro cp1252 in modo non distruttivo: prima traslittera i
 * caratteri "sosia" verso l'equivalente rappresentabile, poi rimuove cio' che
 * non ha equivalente. Accenti italiani, euro, trattini tipografici, virgolette
 * curve, bullet, ellissi e il segno "x" di moltiplicazione sono GIA' dentro
 * cp1252: vengono lasciati intatti e non devono essere traslitterati.
 *
 * NB: nelle regex i caratteri sono scritti con escape \uXXXX e NON in forma
 * letterale. Oltre a renderli leggibili, e' necessario: U+2028 e U+2029 sono
 * terminatori di riga per JavaScript e, se inseriti letteralmente in un regex
 * literal, spezzano il sorgente e rompono la build.
 */

// Larghezza zero: sopravvivono a trim() e a \s, sballano larghezze e confronti.
// ZWSP, ZWNJ, ZWJ, word joiner, BOM/ZWNBSP, soft hyphen.
// Rimuovere ZWNJ/ZWJ e' voluto: non sono rappresentabili in cp1252 e le sequenze
// emoji che uniscono vengono comunque scartate dal filtro finale.
// eslint-disable-next-line no-misleading-character-class
const ZERO_WIDTH = /[\u200B\u200C\u200D\u2060\uFEFF\u00AD]/g;

// Spazi esotici -> spazio normale.
// NBSP (U+00A0) e' dentro cp1252, ma lo normalizziamo comunque: in PDF non offre
// alcun vantaggio e rende diversi due valori visivamente identici lato dati.
const EXOTIC_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

// Separatori di riga Unicode -> newline standard (gestito da autoTable).
const LINE_SEPARATORS = /[\u2028\u2029]/g;

// Controlli C0/C1. Newline e tab sono esclusi dalla classe: restano legittimi.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g;

/**
 * Sosia FUORI cp1252 -> equivalente rappresentabile.
 * Coprono i casi realistici: separatori scritti a codice e incolla da Word/Excel/web.
 * Chi e' gia' rappresentabile in cp1252 NON va inserito qui.
 */
const TRANSLITERATIONS = new Map([
  // Frecce. → e' il separatore usato a codice nelle descrizioni articolo.
  ["→", "-"],
  ["←", "-"],
  ["↔", "-"],
  ["⇒", "=>"],
  ["⇐", "<="],
  ["⇔", "<=>"],
  ["▶", ">"],
  ["➤", ">"],
  ["➜", "->"],
  // Trattini fuori cp1252 (‐ / ‑ arrivano spesso da Word).
  ["‐", "-"],
  ["‑", "-"],
  ["‒", "-"],
  ["―", "-"],
  ["−", "-"],
  // Virgolette/apostrofi non cp1252 (‘ ’ “ ” ‚ lo sono).
  ["‛", "'"],
  ["‟", '"'],
  ["′", "'"],
  ["″", '"'],
  ["‵", "'"],
  // Segni matematici e unita' di misura ricorrenti.
  ["⁄", "/"],
  ["∕", "/"],
  ["≈", "~"],
  ["≤", "<="],
  ["≥", ">="],
  ["≠", "!="],
  ["㎡", "mq"],
  ["㎥", "mc"],
  ["㎝", "cm"],
  ["㎖", "ml"],
  // Simboli decorativi che finiscono nei nomi articolo incollati.
  ["✓", "v"],
  ["✔", "v"],
  ["✗", "x"],
  ["✘", "x"],
  ["●", "-"],
  ["○", "-"],
  ["■", "-"],
  ["▪", "-"],
  ["‣", "-"],
  // Box drawing (bordi incollati da tabelle).
  ["─", "-"],
  ["━", "-"],
  ["│", "|"],
  ["┼", "+"],
  // Puntini.
  ["⋯", "..."],
  ["‧", "."],
]);

/**
 * Blocco 0x80-0x9F di cp1252: gli unici codepoint sopra Latin-1 che WinAnsi mappa
 * (euro, virgolette curve, en/em dash, bullet, ellissi, trademark...).
 */
const CP1252_HIGH_BLOCK = new Set([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030,
  0x0160, 0x2039, 0x0152, 0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022,
  0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0x017e, 0x0178,
]);

/** Un codepoint e' scrivibile da jsPDF con i font standard (WinAnsiEncoding)? */
function isWinAnsiRepresentable(codePoint) {
  if (codePoint === 0x0a || codePoint === 0x09) return true; // newline / tab
  if (codePoint >= 0x20 && codePoint <= 0x7e) return true; // ASCII stampabile
  if (codePoint >= 0xa0 && codePoint <= 0xff) return true; // Latin-1
  return CP1252_HIGH_BLOCK.has(codePoint);
}

/**
 * Rende una stringa sicura per jsPDF con i font standard.
 * @param {*} value valore di partenza (convertito a stringa)
 * @param {{ fallback?: string }} [options] valore restituito se il risultato e' vuoto
 * @returns {string}
 */
export function pdfSafe(value, options = {}) {
  const { fallback = "" } = options;
  if (value === null || value === undefined) return fallback;

  let text = String(value);
  if (!text) return fallback;

  // NFC ricompone "lettera + accento combinante" in un singolo carattere Latin-1:
  // senza, una "citta'" digitata con accenti combinanti perderebbe l'accento.
  if (typeof text.normalize === "function") text = text.normalize("NFC");

  text = text
    .replace(/\r\n?/g, "\n")
    .replace(LINE_SEPARATORS, "\n")
    .replace(ZERO_WIDTH, "")
    .replace(EXOTIC_SPACES, " ")
    .replace(CONTROL_CHARS, "");

  let out = "";
  for (const char of text) {
    const replacement = TRANSLITERATIONS.get(char);
    if (replacement !== undefined) {
      out += replacement;
      continue;
    }
    if (isWinAnsiRepresentable(char.codePointAt(0))) {
      out += char;
      continue;
    }
    // Nessun equivalente (emoji, alfabeti non latini, simboli rari): scartare il
    // singolo carattere e' preferibile a corrompere l'intera cella.
  }

  // La normalizzazione puo' lasciare spazi doppi o separatori orfani
  // (es. "Marmo - " quando il secondo membro era vuoto).
  return (
    out
      .replace(/[ \t]{2,}/g, " ")
      .replace(/[ \t]+$/gm, "")
      .trim() || fallback
  );
}

/**
 * Applica pdfSafe a una cella di jspdf-autotable, preservando la forma a oggetto
 * ({ content, colSpan, styles }) usata per le intestazioni di sezione.
 */
export function pdfSafeCell(cell) {
  if (cell === null || cell === undefined) return "";
  if (typeof cell === "object" && !Array.isArray(cell)) {
    return "content" in cell
      ? { ...cell, content: pdfSafe(cell.content) }
      : cell;
  }
  return pdfSafe(cell);
}

/** Applica pdfSafeCell a tutte le celle di un body/head di jspdf-autotable. */
export function pdfSafeRows(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) =>
    Array.isArray(row) ? row.map(pdfSafeCell) : pdfSafeCell(row)
  );
}

export default pdfSafe;
